import React, { useContext } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { css } from '@emotion/react';
import uuid from 'react-uuid';

// redux
import { getFlatCourseAliases } from 'redux/selectors/course';
import {
  createAiCharacterObjection,
  deleteAiCharacterObjection,
} from 'redux/actions/ai-role-play';

import { doubleSpacing, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import {
  ReactComponent as AICharacter,
} from 'styles/icons/ai-characters.svg';
import NvIcon from 'shared/components/nv-icon';
import ObjectiveInputList from './objective-input-list';
import { AIPersonalities } from '..';
import EditAiInstructionContext from './context';
import useAutoReplaceFields from '../../hooks/use-auto-replace-fields';

const styles = css`
  .learner-role-input {
    width: calc(50% - ${halfSpacing}px);
  }
  .gap-2 {
    gap: ${standardSpacing}px;
  }
  .personality-dropdown-button {
    width: calc(50% - ${halfSpacing}px);
    .bs4-dropdown {
      .title {
        height: 45px;
    }
  }
`;

const getUpdatedStatus = (fieldName) => {
  const updatedText = () => {
    switch (fieldName) {
      case 'aiCharacterRole':
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_ROLE_UPDATED();
      case 'aiCharacterName':
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_NAME_UPDATED();
      default:
        return '';
    }
  };
  return (
    <div className='d-flex mt-2'>
      <NvIcon
        size='xss-smallest'
        icon='complete'
        className='text-success d-flex align-items-center'
      />
      <p className='mb-0 ml-1'>
        {updatedText()}
      </p>
    </div>
  );
};

const getPersonalityLabel = (personality: AIPersonalities) => {
  switch (personality) {
    case AIPersonalities.IRRITABLE:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.IRRITABLE();
    case AIPersonalities.DISINTERESTED:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.DISINTERESTED();
    case AIPersonalities.NEUTRAL:
    default:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.NEUTRAL();
  }
};

const CharacterSetting = () => {
  const params = useLecturePageParams();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const personalities = [AIPersonalities.NEUTRAL, AIPersonalities.IRRITABLE, AIPersonalities.DISINTERESTED];

  const {
    setValue,
    trigger,
    getValues,
    control,
  } = useFormContext();
  const { aiRolePlayId } = useContext(EditAiInstructionContext) || {};
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [isAddingNewAiObjective, setIsAddingNewAiObjective] = React.useState(false);
  const dispatch = useDispatch();

  // Hook for auto-replacing values across form fields when aiCharacterRole or aiCharacterName changes
  const {
    handleAiCharacterRoleBlur,
    handleAiCharacterNameBlur,
    updatedFields,
  } = useAutoReplaceFields();

  const { fields, append } = useFieldArray({
    name: 'aiCharacterObjections',
    control,
    keyName: 'fieldId',
  });

  const items: NvDropdownOption[] = personalities.map((personality, index) => ({
    id: index,
    text: getPersonalityLabel(personality),
    type: 'text',
    textClassName: 'text-large-body',
    callback: () => {
      setValue('aiCharacterPersonality', personality, { shouldDirty: true });
      trigger('aiCharacterPersonality');
      setShowDropdown(false);
    },
  }));
  const initialIndex = personalities.indexOf(getValues().aiCharacterPersonality);
  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex);

  // We mutate control._defaultValues directly because RHF v7.22.1 doesn't support
  // shouldDirty: false on field array methods (append, remove) (added in v7.36.0). Public alternatives
  // like resetField reset both value and default (wiping pending text edits), and
  // reset(getValues(), { keepDirtyValues: true }) requires v7.31.0+. This is a
  // pragmatic tradeoff: it re-baselines defaults after already-saved structural
  // changes so isDirty reflects only unsaved text edits. Should be replaced with
  // native shouldDirty: false on append/remove if RHF is upgraded to v7.36.0+.
  const updateObjectionsDefaultValue = () => {
    const currentObjections = getValues('aiCharacterObjections') || [];
    // eslint-disable-next-line no-underscore-dangle
    control._defaultValues.aiCharacterObjections = currentObjections.map((obj) => ({ ...obj }));
  };

  const handleAddAiObjection = () => {
    if (aiRolePlayId) {
      setIsAddingNewAiObjective(true);
      dispatch(createAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        text: '',
      })).then((res) => {
        if (res) {
          // Use setValue instead of append to avoid marking the field as dirty,
          // since the objection is already saved to the backend
          const current = getValues('aiCharacterObjections') || [];
          setValue('aiCharacterObjections', [...current, { id: res.payload.id, text: '' }], { shouldDirty: false });
          updateObjectionsDefaultValue();
        }
      }).finally(() => {
        setIsAddingNewAiObjective(false);
      });
    } else {
      append({ id: uuid(), text: '' });
    }
  };

  const handleRemoveAiObjection = (aiCharacterObjectionId) => {
    if (fields.length <= 0) return;
    // Use setValue instead of remove to avoid marking the field as dirty,
    // since the deletion is already saved to the backend
    const current = getValues('aiCharacterObjections') || [];
    setValue(
      'aiCharacterObjections',
      current.filter((field) => field.id !== aiCharacterObjectionId),
      { shouldDirty: false },
    );
    updateObjectionsDefaultValue();
    if (aiRolePlayId && aiCharacterObjectionId) {
      dispatch(deleteAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        aiCharacterObjectionId,
      }));
    }
  };

  return (
    <div css={styles}>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-center mt-8'>
          <AICharacter
            height={doubleSpacing}
            width={doubleSpacing}
          />
        </div>
        <p className='page-title text-large mb-4 mt-2 mx-auto text-black lh-35'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.THE_CHARACTERS()}
        </p>
      </div>
      <div>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARNER_ROLE(courseAliases)}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WHAT_ROLE_PLAYING()}
        </p>
        <NvTextInput
          withForm
          name='learnerRole'
          className='border-gray-5 bg-white mt-2 learner-role-input required-input'
          inputClassName='text-large-body lh-25'
          required
        />
      </div>
      <div className='d-flex w-100 mt-4 gap-2'>
        <div className='w-50'>
          <p className='bold text-large-body mb-0 lh-25'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_ROLE()}
          </p>
          <NvTextInput
            withForm
            name='aiCharacterRole'
            className='border-gray-5 bg-white mt-2 required-input'
            inputClassName='text-large-body lh-25'
            required
            onBlur={handleAiCharacterRoleBlur}
          />
          {updatedFields.aiCharacterRole && getUpdatedStatus('aiCharacterRole')}
        </div>
        <div className='w-50'>
          <p className='bold text-large-body mb-0 lh-25'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_NAME()}
          </p>
          <NvTextInput
            withForm
            name='aiCharacterName'
            className='border-gray-5 bg-white mt-2 required-input'
            required
            inputClassName='text-large-body lh-25'
            onBlur={handleAiCharacterNameBlur}
          />
          {updatedFields.aiCharacterName && getUpdatedStatus('aiCharacterName')}
        </div>
      </div>
      <div className='mt-4'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_BEHAVE()}
        </p>
        <div className='personality-dropdown-button text-large-body'>
          <NvDropdown
            showSelectedIndicator
            buttonStyle={NvDropdownButtonStyle.FORM}
            items={items}
            initialIndex={selectedIndex}
            show={showDropdown}
            menuClassName='w-100'
            onToggle={(isOpen) => setShowDropdown(isOpen)}
          />
        </div>
      </div>
      <div className='mt-4'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_OBJECTIONS()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_SAY()}
        </p>
        <ObjectiveInputList
          name='aiCharacterObjections'
          fields={fields}
          handleAddField={handleAddAiObjection}
          handleRemoveField={handleRemoveAiObjection}
          isAddingField={isAddingNewAiObjective}
          maxFieldCount={5}
        />
      </div>
    </div>
  );
};

export default CharacterSetting;
